/**
 * Pure helpers for the Seva Mandir V1 godown screens (alignment map V1-F5 boards). The phone
 * rules themselves stay in ../rules.ts; these only shape what the boards show.
 */
import type { MessageKey } from '@/i18n'
import type { StoreLot, Trip } from '@/offline/storeCache'
import { fmtDate, fmtTime, fmtWhen, shortLot, tr } from '@/features/gate/v1/text'
import { lastNet, type ReceiveScan } from '../rules'
import type { BagView, StoreState, TripView } from '../projection'

/** How long ago, in the unit the boards use ("40 मिनट", "2 घंटे", "3 दिन"). */
export function sinceParts(ms: number): { unit: 'min' | 'hour' | 'day'; n: number } {
  const min = Math.max(0, Math.floor(ms / 60_000))
  if (min < 60) return { unit: 'min', n: min }
  const hours = Math.floor(min / 60)
  if (hours < 48) return { unit: 'hour', n: hours }
  return { unit: 'day', n: Math.floor(hours / 24) }
}

/**
 * Which trip this is, so two trucks with one number differ: the shipment code's last part and
 * when it left ("C1-01 · Today 7:40"; an older one "12 October 7:40").
 */
export function tripWhich(
  t: { code: string; dispatched_at?: string | null },
  now = Date.now(),
): string {
  const parts = [shortLot(t.code)]
  if (t.dispatched_at) {
    const at = new Date(t.dispatched_at).getTime()
    const when = fmtWhen(at, now)
    parts.push(when === fmtDate(at) ? `${when} ${fmtTime(at)}` : when)
  }
  return parts.join(' · ')
}

/** "MP09 gh  4471" → "MP09 GH 4471": one way of writing a number plate. */
export const normVehicle = (v: string) => v.trim().toUpperCase().replace(/\s+/g, ' ')

/** The last four digits of a number plate ("MP 09 GH 4471" → "4471"); '' when it has fewer. */
export function last4(vehicle: string | null | undefined): string {
  const d = (vehicle ?? '').replace(/\D/g, '')
  return d.length >= 4 ? d.slice(-4) : ''
}

/** The number printed under a tag ("RJ-BELU-0042" → "0042"). */
const printed = (payload: string) => payload.split(/[-/]/).at(-1) ?? payload

/**
 * A typed tag number → the one code it names among `payloads`: the whole code, or the number
 * printed under it ("2205" or "02205" for "…-02205", "154" for "…-SA154"). Null when none or
 * more than one match.
 */
export function matchTypedCode(payloads: readonly string[], typed: string): string | null {
  const t = typed.trim().toUpperCase()
  if (!t) return null
  const exact = payloads.find((p) => p.toUpperCase() === t)
  if (exact) return exact
  if (!/^\d+$/.test(t)) return null
  const n = Number(t)
  const hits = [...new Set(payloads)].filter((p) => {
    const digits = /(\d+)$/.exec(printed(p))?.[1]
    return digits !== undefined && Number(digits) === n
  })
  return hits.length === 1 ? hits[0]! : null
}

/** Trips coming to this godown and not yet received here, the longest on the road first. */
export function arrivingTrips(state: Pick<StoreState, 'trips' | 'siteId'>): TripView[] {
  return [...state.trips.values()]
    .filter(
      (t) =>
        t.to_location_id === state.siteId &&
        t.status === 'IN_TRANSIT' &&
        t.local_status !== 'RECEIVED' &&
        t.local_status !== 'RECEIVED_SHORT',
    )
    .sort((a, b) => (a.dispatched_at ?? '').localeCompare(b.dispatched_at ?? ''))
}

export const tripBags = (trip: Pick<Trip, 'items'>) =>
  trip.items.filter((i) => i.container_id).length

export interface RecentVehicle {
  vehicle_no: string
  /** When it last left (ISO), when the phone knows. */
  at: string | null
}

/**
 * Vehicles to pick from at loading: those this phone typed before and the trips the list knows
 * leaving this place, newest first, one line per number plate.
 */
export function recentVehicles(
  trips: Iterable<Pick<Trip, 'vehicle_no' | 'dispatched_at' | 'from_location_id'>>,
  fromId: string,
  remembered: readonly RecentVehicle[],
): RecentVehicle[] {
  const by = new Map<string, RecentVehicle>()
  const add = (v: RecentVehicle) => {
    const key = normVehicle(v.vehicle_no)
    if (!key) return
    const prev = by.get(key)
    if (!prev || (v.at ?? '') > (prev.at ?? '')) by.set(key, { vehicle_no: key, at: v.at })
  }
  for (const t of trips)
    if (t.from_location_id === fromId && t.vehicle_no)
      add({ vehicle_no: t.vehicle_no, at: t.dispatched_at ?? null })
  remembered.forEach(add)
  return [...by.values()].sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''))
}

/**
 * Lots, bags and kilos of what is on the vehicle (the dispatch sheet). Each bag counts at its last
 * weight: a run's output sack has no gate weight.
 */
export function loadTotals(
  bags: readonly Pick<BagView, 'unit_id' | 'net_g_current' | 'net_g_gate'>[],
) {
  return {
    lots: new Set(bags.map((b) => b.unit_id)).size,
    bags: bags.length,
    netG: bags.reduce((s, b) => s + (lastNet(b) ?? 0), 0),
  }
}

/** Bags scanned off with no list yet (a vehicle the godown phone did not know), by plate digits. */
export interface HeldGroup {
  digits: string
  scans: ReceiveScan[]
}

/**
 * The held scans that belong to `trip` — the group whose four digits are the trip's plate, and any
 * scan of a tag on the trip's list — and the groups left after taking them.
 */
export function takeHeld(
  groups: readonly HeldGroup[],
  trip: Pick<Trip, 'vehicle_no' | 'items'>,
): { scans: ReceiveScan[]; rest: HeldGroup[] } {
  const digits = last4(trip.vehicle_no)
  const onTrip = new Set(trip.items.map((i) => i.qr_payload.toUpperCase()))
  const scans: ReceiveScan[] = []
  const rest: HeldGroup[] = []
  for (const g of groups) {
    if (digits && g.digits === digits) {
      scans.push(...g.scans)
      continue
    }
    const mine = g.scans.filter((s) => onTrip.has(s.qr_payload.toUpperCase()))
    scans.push(...mine)
    const left = g.scans.filter((s) => !onTrip.has(s.qr_payload.toUpperCase()))
    if (left.length) rest.push({ ...g, scans: left })
  }
  return { scans, rest }
}

/** Adds scans not already in `into` (by code), in order. */
export function mergeScans(into: readonly ReceiveScan[], more: readonly ReceiveScan[]) {
  const seen = new Set(into.map((s) => s.qr_payload.toUpperCase()))
  const out = [...into]
  for (const s of more) {
    const k = s.qr_payload.toUpperCase()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(s)
  }
  return out
}

/**
 * Bags waiting on the dock: STORED, on no trip, with their place the site itself (unloaded, not
 * yet put in a bay or spot).
 */
export function dockBags<B extends Pick<BagView, 'status' | 'shipment_id' | 'location_id'>>(state: {
  bags: Map<string, B>
  locations: Map<string, { level: string }>
}): B[] {
  return [...state.bags.values()].filter(
    (b) =>
      b.status === 'STORED' &&
      !b.shipment_id &&
      !!b.location_id &&
      state.locations.get(b.location_id)?.level === 'SITE',
  )
}

/** Hold reasons the keeper gets in plain words; anything else reads as a plain office hold. */
const HOLD_REASON_KEYS = {
  STORAGE_RISK: 'store.v1.hold.reason.storage',
  HOME_STORAGE_UNKNOWN: 'store.v1.hold.reason.storage',
  PAPER_DAY: 'store.v1.hold.reason.paper',
  LAB_FAIL: 'store.v1.hold.reason.labFail',
  LAB_TEST: 'store.v1.hold.reason.labTest',
  QUALITY: 'store.v1.hold.reason.quality',
  QUALITY_CHECK: 'store.v1.hold.reason.quality',
  MOISTURE_HIGH: 'store.v1.hold.reason.moisture',
  WEIGHT_CHECK: 'store.v1.hold.reason.weight',
  WEIGHT_OUT_OF_RANGE: 'store.v1.hold.reason.weight',
  COUNT_MISMATCH: 'store.v1.hold.reason.count',
  BAG_COUNT_MISMATCH: 'store.v1.hold.reason.count',
  TRANSIT_SHORTAGE: 'store.v1.hold.reason.count',
  RUN_EXCEPTION: 'store.v1.hold.reason.run',
  RUN_MIXED: 'store.v1.hold.reason.run',
} as const satisfies Record<string, MessageKey>

/**
 * The message keys for a lot's hold reason(s) — the server may join several with commas — in
 * order, each once; unknown reasons are left out.
 */
export function holdReasonKeys(reason: string | null | undefined): MessageKey[] {
  const keys = (reason ?? '')
    .split(',')
    .map((r) => r.trim().toUpperCase())
    .map((r) => (HOLD_REASON_KEYS as Record<string, MessageKey>)[r])
    .filter((k): k is MessageKey => !!k)
  return [...new Set(keys)]
}

/**
 * One plain line for a lot the office has stopped ("ऑफ़िस ने यह लॉट रोका है: भंडार का जोखिम"),
 * or null when the lot is not on hold.
 */
export function lotHoldLine(
  lot: Pick<StoreLot, 'hold' | 'hold_reason'> | null | undefined,
): string | null {
  if (!lot || lot.hold === 'NONE') return null
  const reasons = holdReasonKeys(lot.hold_reason).map((k) => tr(k))
  return reasons.length
    ? tr('store.v1.hold.line', { reason: reasons.join(', ') })
    : tr('store.v1.hold.lineNoReason')
}

/**
 * Runs `fn` at most once per `gapMs` and never twice at once; a call inside the gap or while one is
 * running does nothing. Failures are swallowed (the caller keeps what it has).
 */
export function throttledRunner(
  fn: () => Promise<unknown>,
  gapMs: number,
  clock: () => number = Date.now,
): () => Promise<void> {
  let running = false
  let last = -Infinity
  return async () => {
    const at = clock()
    if (running || at - last < gapMs) return
    running = true
    last = at
    try {
      await fn()
    } catch {
      // Silent: offline or a failed download keeps the cache this phone has.
    } finally {
      running = false
    }
  }
}
